<template>
    <div class="w-full">
        <reports-table
            sortable="custom"
            :tableData="tableData"
            @sort-change="onSortChange"
            @on-create-report="addCreateTab"
        >
            <template v-slot:expand-content="{row}">
                <div class="border-l-5 p-4 border-primary">
                    <div class="m-2 text-lg font-medium main-text-color">{{ $t('widget.scheduleList') }}</div>
                    <div class="grid grid-cols-2 gap-4">
                        <schedule-card
                            v-for="(trigger, index) in filterDataByType(row.ReportTriggerList)"
                            :data="trigger"
                            showBtnSendNow
                            :reportData="row"
                            :key="index">
                        </schedule-card>
                    </div>
                </div>
            </template>
            <template v-slot:ReportID="{row}">
                <div class="flex justify-center">
                    {{ get(row, 'ReportID', '') }}
                </div>
            </template>
            <template v-slot:ReportName="{row}">
                <div class="flex justify-center">
                    {{ get(row, 'ReportName', '') }}
                </div>
            </template>
            <template v-slot:ReportItemList="{row}">
                <template v-if="row.ReportItemList && filterDataByType(row.ReportItemList).length">
                    <delimited-list class="flex items-center justify-center" :list="filterDataByType(get(row, 'ReportItemList', []))" :limit="5" separator=",">
                        <template v-slot:list-item="{item}">
                            <rec-item
                                :item="item"
                                :name="translateTemplateNameContentTag(item)"
                                firstItem
                            />
                        </template>
                        <template v-slot:other-icon="{data}">
                            <custom-tag content-class="icon-ellipsis">+ {{ data }}</custom-tag>
                        </template>
                        <template v-slot:other-content="{list}">
                            <div class="max-w-lg">
                                <div
                                    v-for="(item, index) in list" :key="index"
                                    class="flex-1 items-center justify-start p-1"
                                >
                                    <custom-tag>
                                        {{ translateTemplateNameContentTag(item) }}
                                    </custom-tag>
                                </div>
                            </div>
                        </template>
                    </delimited-list>
                </template>
                <template v-else >
                    <div class="flex items-center justify-center w-full">
                        <span class="quick-add-button" :class="loading ? 'pointer-events-none cursor-wait' : 'cursor-pointer'">
                            <span class="mx-2">
                                <div class="flex items-center text-primary" @click="openAddWidgetModal(row)">
                                    <i class="icon-base add-schedule-btn-text vc-icon-plus-linear" />
                                    <span class="text-main-sm leading-4">
                                        {{ $t('dashboard.addWidget') }}
                                    </span>
                                </div>
                            </span>
                        </span>
                    </div>
                </template>
            </template>

            <template v-slot:ReportTriggerList="{row}">
                <template v-if="row.ReportTriggerList && filterDataByType(row.ReportTriggerList).length">
                    <delimited-list class="flex items-center justify-center" :list="filterDataByType(get(row, 'ReportTriggerList', []))" :limit="4" separator=",">
                        <template v-slot:list-item="{item}">
                            <rec-item
                                :item="item"
                                :name="get(item, 'ReportTriggerName', '- -')"
                                firstItem
                            />
                        </template>
                        <template v-slot:other-icon="{data}">
                            <custom-tag content-class="icon-ellipsis">+ {{ data }}</custom-tag>
                        </template>
                        <template v-slot:other-content="{list}">
                            <div class="max-w-xs">
                                <div v-for="(item, index) in list" :key="index"
                                    class="flex-1 items-center justify-start p-1">
                                    <custom-tag>
                                        {{ get(item, 'ReportTriggerName', '- -') }}
                                    </custom-tag>
                                </div>
                            </div>
                        </template>
                    </delimited-list>
                </template>
                <template v-else>
                        <div class="flex items-center justify-center w-full">
                            <span class="mx-2 quick-add-button">
                                <schedule-form
                                    :buttonLabel="$t('widget.addSchedule')"
                                    icon="vc-icon-plus-linear"
                                    :reportId="row.ReportID"
                                    @addedSchedule="reloadData"
                                    :data="row"
                                    :title="$t('widget.addSchedule')"
                                />
                            </span>
                        </div>
                </template>
            </template>

            <template v-slot:ReportStatusID="{row}">
                <div class="flex items-center justify-center">
                    <status-button
                        v-if="row.ReportStatusID === REPORT_STATUS_ID_ENABLED"
                        :label="$t('report.wizard.enabled')"
                        type="blue"
                        @on-click="changeReportStatus(row, REPORT_STATUS_ID_DISABLED)"
                        key="enabled"
                    />
                    <status-button
                        v-else
                        :label="$t('report.switch.disable')"
                        type="danger"
                        @on-click="changeReportStatus(row, REPORT_STATUS_ID_ENABLED)"
                        key="disabled"
                    />
                </div>
            </template>

            <template v-slot:ReportExportTypes="{row}">
                <div class="flex items-center justify-center">
                    <i v-if="row.ReportExportTypes.includes('xlsx')" class="vc-icon-excel icon-lg mx-2 text-primary" />
                    <i v-if="row.ReportExportTypes.includes('email')" class="vc-icon-mail icon-lg mx-2 text-primary" />
                </div>
            </template>
            <template v-slot:action="{row}">
                <div class="flex items-center justify-center">
                    <button-icon 
                        icon="vc-icon-copy"
                        :description="$t('report.tooltip.copyReport')"
                        @click="onReportCopy(row)"
                        class="copy-report-button"
                    />
                    <button-icon
                        icon="vc-icon-edit-pencil"
                        :description="$t('report.tooltip.editReport')"
                        @click="onReportEdit(row)"
                        class="edit-report-button"
                    />
                    <button-icon
                        icon="vc-icon-recycle-bin"
                        :description="$t('report.tooltip.deleteReport')"
                        type="danger"
                        @click="onReportDelete(row)"
                        class="delete-report-button"
                    />
                </div>
            </template>
        </reports-table>
        <delete-dialog
            v-if="showDeleteDialog"
            :visible.sync="showDeleteDialog"
            :config="{descriptionIcon: ''}"
            :description="$t('report.deleteReportConfirmation')"
            @on-close="onCancelDelete"
            @on-cancel="onCancelDelete"
            @on-confirm="deleteReport"/>
        <copy-report-dialog
            v-if="showCopyDialog"
            :visible.sync="showCopyDialog"
            :report-copy-name.sync="reportCopyName"
            :is-duplicate-schedules.sync="isDuplicateSchedules"
            :is-duplicate-widgets.sync="isDuplicateWidgets"
            @on-close="onCancelCopy"
            @on-cancel="onCancelCopy"
            @on-confirm="copyReport"
        />
        <add-widgets-dialog
            :visible.sync="visible"
            :reportId="reportId"
            @added-widgets="reloadData"
            :widgets="widgetsToAdd"
        />
    </div>
</template>

<script>
import { Table, TableColumn, Tooltip, Card, Notification } from 'element-ui'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'

import CopyReportDialog from "@/modules/reports/components/CopyReportDialog";
import DeleteDialog from "@/components/Dialogs/DeleteDialog";

import {reportApi} from "../services/reportService"
import BaseButton from "@/components/Common/Buttons/BaseButton";
import ButtonIcon from "@/modules/common/components/ButtonIcon";
import ReportsTable from "@/modules/reports/components/ReportsTable";
import RecItem from "@/modules/reports/components/RecItem";
import DelimitedList from "@/modules/reports/components/DelimitedList";
import CustomTag from "@/modules/reports/components/Tag"
import ScheduleCard from "@/modules/reports/components/ScheduleCard";
import ScheduleForm from '@/modules/reports/components/schedule/ScheduleForm.vue'
import AddWidgetsDialog from '@/components/Reports/Widgets/AddWidgetsDialog.vue'
import {
    REPORT_ITEM_STATUS_ID_DISABLED,
    REPORT_TRIGGER_STATUS_ID_DISABLED,
    REPORT_STATUS_ID_ENABLED,
    REPORT_STATUS_ID_DISABLED,
    REPORT_STATUS_ID_DELETE,
    ReportExportXLSX,
    ReportExportEmail,
    ReportExportTypeIDs
} from '@/enum/reportStatuses'
import StatusButton from "@/components/Common/Buttons/StatusButton"
import { WidgetApi } from '@/api/widgetApi'
import { setTimezoneData } from '@/enum/reportStaticData'

export default {
    name: "reports-list",
    components: {
        BaseButton,
        [Table.name]: Table,
        [Tooltip.name]: Tooltip,
        [TableColumn.name]: TableColumn,
        [Card.name]: Card,
        ButtonIcon,
        ReportsTable,
        RecItem,
        DelimitedList,
        CustomTag,
        ScheduleCard,
        DeleteDialog,
        CopyReportDialog,
        ScheduleForm,
        AddWidgetsDialog,
        StatusButton
    },
    props: {
        reportItemForEdit: {
            type: Object,
            default: () => {}
        },
        currentAccountId: {
            type: Number
        }
    },
    data() {
        return {
            tableData: [],
            showDeleteDialog: false,
            reportToDelete: null,
            showCopyDialog: false,
            reportToCopy: null,
            reportCopyName: '',
            isDuplicateWidgets: true,
            isDuplicateSchedules: true,
            visible: false,
            reportId: null,
            REPORT_STATUS_ID_ENABLED,
            REPORT_STATUS_ID_DISABLED,
            widgetsToAdd: [],
            loading: true
        }
    },
    watch: {
        reportItemForEdit: {
            handler (val) {
                if (Object.keys(val).length) {
                    this.tableData = this.tableData.map(el => {
                        if (el.ReportID === val.ReportID) {
                            el = val
                        }
                        return el
                    })
                }
            },
            deep: true,
            immediate: true
        },
        async currentAccountId (val) {
            if (val) {
                this.tableData = await this.loadData() 
                this.widgetsToAdd = await this.getAllWidgets()
            }
        }
    },
    computed: {
        widgetTemplateList () {
            return this.$store.getters['reportTrigger/getConfData'].WidgetTemplateList.filter(el => el.ReportExportData)
        }
    },
    methods: {
        get,
        addCreateTab() {
            this.$emit('on-create-report')
        },
        onReportEdit(row) {
            this.$emit('on-edit-row', row)
        },
        onReportCopy(row) {
            this.reportToCopy = row
            if (!this.reportToCopy) return
            this.showCopyDialog = true
        },
        onReportDelete(row) {
            this.reportToDelete = get(row, 'ReportID', null)
            if (!this.reportToDelete) return
            this.showDeleteDialog = true
        },
        async deleteReport() {
            try {
                await this.$store.dispatch('dashboards/setContentLoading', true)
                const payload = {
                    ReportID: this.reportToDelete,
                    ReportStatusID: REPORT_STATUS_ID_DELETE
                }
                await reportApi.changeStatus(payload)
                this.reportToDelete = null
                this.showDeleteDialog = false

                const reports = await this.getReportsList()
                this.tableData = reports
            } catch (err) {
                console.error(err)
            } finally {
                await this.$store.dispatch('dashboards/setContentLoading', false)
            }
        },
        async copyReport() {
            try {
                await this.$store.dispatch('dashboards/setContentLoading', true)

                let newReport = cloneDeep(this.reportToCopy)
                if (!this.isDuplicateSchedules && !this.isDuplicateWidgets) {
                    newReport.ReportItemList = []
                    newReport.ReportTriggerList = []
                } else if (!this.isDuplicateSchedules && this.isDuplicateWidgets) {
                    newReport.ReportTriggerList = []
                } else if (this.isDuplicateSchedules && !this.isDuplicateWidgets) {
                    let triggerWidgetIds = []
                    newReport.ReportTriggerList.forEach(trigger => {
                        trigger.ReportTriggerCondition.forEach(condition => {
                            condition.ReportTriggerConditionFilter.forEach(filter => {
                                triggerWidgetIds.push(filter.WidgetID)
                            })
                        })
                    })

                    const reportWidgets = newReport.ReportItemList.filter(el => {
                        return triggerWidgetIds.includes(el.WidgetID)
                    })

                    newReport.ReportItemList = reportWidgets
                }

                delete newReport.ReportID

                newReport.ReportItemList.forEach(el => {
                    delete el.ReportID
                    delete el.ReportItemID

                    el.ReportItemExport.forEach(item => {
                        delete item.ReportItemExportID
                    })
                })

                newReport.ReportTriggerList.forEach(el => {
                    delete el.ReportID
                    delete el.ReportTriggerID

                    el.ReportTriggerCondition.forEach(trigger => {
                        delete trigger.ReportTriggerID
                        delete trigger.ReportTriggerConditionID
                        trigger.ReportTriggerConditionFilter.forEach(trigger => {
                            delete trigger.ReportTriggerConditionID
                            delete trigger.ReportTriggerConditionFilterID
                        })
                    })

                    el.ReportRecipient.forEach(rec => {
                        delete rec.RecipientID
                        delete rec.ReportID
                        delete rec.ReportRecipientID
                        delete rec.ReportTriggerID
                    })
                })

                if (this.reportCopyName) {
                    newReport.ReportName = this.reportCopyName
                } else {
                    newReport.ReportName += '-copy'
                }

                await reportApi.store(newReport)
                this.reportToCopy = null
                this.showCopyDialog = false

                const reports = await this.getReportsList()
                this.tableData = reports
            } catch (err) {
                console.error(err)
            } finally {
                await this.$store.dispatch('dashboards/setContentLoading', false)
            }
        },
        onCancelDelete() {
            this.reportToDelete = null
            this.showDeleteDialog = false
        },
        onCancelCopy() {
            this.reportToCopy = null
            this.showCopyDialog = false
        },
        onExportAsPdf(row) {
            alert('Export as PDF')
        },
        onExportAsCSV(row) {
            alert('Export as CSV')
        },
        onExportAsHtml(row) {
            alert('Export as HTML')
        },
        async getReportsList() {
            const payload = {
                ReportStatusID: [REPORT_STATUS_ID_ENABLED, REPORT_STATUS_ID_DISABLED],
                AccountID: [this.currentAccountId]
            }

            const reportsList = await reportApi.list(payload)
            return reportsList.filter(el => el.ReportStatusID !== REPORT_STATUS_ID_DELETE)
                .map(el => {
                    if (!('timezone' in el.ReportConf)) {
                        el.ReportConf = setTimezoneData()
                    }
                    return el
                })
        },
        onSortChange({column, prop, order}) {
            if (prop === null) {
                return
            }

            if (order === 'ascending') {
                this.tableData.sort((a, b) => (a[prop] > b[prop]) ? 1 : -1)
            } else if (order === 'descending') {
                this.tableData.sort((a, b) => (a[prop] < b[prop]) ? 1 : -1)
            }
        },
        async loadData () {
            const exportTypes = ['xlsx', 'email']

            this.tableData = []
            try {
                await this.$store.dispatch('dashboards/setContentLoading', true)
                const reports = await this.getReportsList()

                return reports.map(el => {
                    el.ReportExportTypes = []

                    exportTypes.forEach(type => {
                        if (this.checkIfReportHasExport(el.ReportItemList, type)) {
                            el.ReportExportTypes.push(type)
                        }
                    })

                    return el
                })
            } catch (e) {
                console.error(e)
            } finally {
                await this.$store.dispatch('dashboards/setContentLoading', false)
            }
        },
        async reloadData () {
            this.tableData = await this.loadData()
        },
        openAddWidgetModal (data) {
            this.reportId = data.ReportID
            this.visible = true
        },
        filterDataByType (data) {
            return data.filter(el => {
                if ('ReportItemStatusID' in el && el.ReportItemStatusID === REPORT_ITEM_STATUS_ID_DISABLED) {
                    return false
                }
                if ('ReportTriggerStatusID' in el && el.ReportTriggerStatusID === REPORT_TRIGGER_STATUS_ID_DISABLED) {
                    return false
                }
                return true
            })
        },
        checkIfReportHasExport (reportItemList, type) {
            if (!reportItemList.length) {
                return false
            }

            const typeOfExport = type === 'xlsx' ? ReportExportXLSX : ReportExportEmail
            let hasValue = false

            reportItemList.forEach(el => {
                if (el.ReportItemExport.length) {
                    hasValue = el.ReportItemExport.some(exportItem => exportItem.ReportExportTypeID === typeOfExport)
                }
            })

            return hasValue
        },
        async changeReportStatus (rowData, typeId) {
            const indexToEdit = this.tableData.findIndex(el => el.ReportID ===  rowData.ReportID)
            this.tableData[indexToEdit].ReportStatusID = typeId
            rowData.ReportStatusID = typeId
            const payload = {
                ReportID: rowData.ReportID,
                ReportStatusID: typeId
            }
            await reportApi.changeStatus(payload)
            Notification.success(this.$t('general.notification.success'))
        },
        async getAllWidgets () {
            let allWidgets = []
            const dashboards = await WidgetApi.getAllWidgetsByAccountId(this.currentAccountId)

            if (!dashboards) {
                return allWidgets
            }

            allWidgets = Object.values(dashboards).map(dashboard => {
                dashboard.isChecked = false
                return dashboard
            })

            const findTheSameWidget = (templateID) => {
                return this.widgetTemplateList.find(el => el.WidgetTemplateID === templateID)
            }

            allWidgets = allWidgets.filter(el => {
                if (!findTheSameWidget(el.TemplateID)) {
                    return false
                }
                const reportExportData = findTheSameWidget(el.TemplateID).ReportExportData
                if (!reportExportData) {
                    return false
                }
                const hasAtLeastOneExportType = () => {
                    return Object.values(reportExportData).some(el=> ReportExportTypeIDs.indexOf(el.ReportExportTypeID) !== -1)
                }
                return hasAtLeastOneExportType()
            })

            this.loading = false

            return allWidgets
        },
        translateTemplateNameContentTag(item) {
            return this.$te(item.TemplateNameContentTag) ? this.$t(item.TemplateNameContentTag) : item.WidgetTitle
        }
    },
    async mounted() {
        if (this.currentAccountId) {
            this.tableData = await this.loadData()
            this.widgetsToAdd = await this.getAllWidgets()
        }
    }
}
</script>

<style scoped lang="scss">
.quick-add-button {
    @apply inline-flex align-middle text-primary;
    padding: 0.2rem 0.4rem;
    border-radius: 1.25rem;
    border: 1px dashed var(--primary-color);
}
.copy-report-button {
    padding: 0px !important;
}
.delete-report-button {
    padding: 0px !important;
}   
.edit-report-button {
    padding: 0px !important;
    margin: 0px 2px !important;
}
.copy-report-button, .edit-report-button, .delete-report-button {
    @apply w-6;
}
::v-deep .el-table__row .el-table__cell .cell {
    padding: 0px !important
}
</style>

<style lang="scss">
.rtl .button-icon {
    margin: 0px !important;
}
</style>
